import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { authService } from '../../services/authService';
import { invoiceService } from '../../services/invoiceService';
import { api } from '../../services/apiClient';
import { imageService } from '../../services/imageService';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { ProgressLoader } from '../ProgressLoader';
import FixOrder from '../FixOrder';

export default function Settings() {
  usePageTitle('Settings');
  const [userRole, setUserRole] = useState<string>('');
  const [userName, setUserName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [showOneDriveConnected, setShowOneDriveConnected] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const agent = authService.getCachedAgent();

        if (!agent) {
          navigate('/login');
          return;
        }

        setUserRole(agent.is_admin ? 'Admin' : 'Sales Agent');
        setUserName(agent.name || agent.id);
        setUserEmail(''); // Not available in agent model
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [navigate]);

  // Get the current settings tab from the URL
  const getCurrentTab = () => {
    const path = location.pathname;
    if (path.includes('/settings/general')) return 'general';
    if (path.includes('/settings/profile')) return 'profile';
    if (path.includes('/settings/notifications')) return 'notifications';
    if (path.includes('/settings/database')) return 'database';
    if (path.includes('/settings/security')) return 'security';
    if (path.includes('/settings/fix-order')) return 'fix-order';
    return 'general'; // default
  };

  const currentTab = getCurrentTab();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('onedrive') === 'connected') {
      setShowOneDriveConnected(true);
      params.delete('onedrive');
      const cleaned = params.toString();
      const nextUrl = cleaned ? `${location.pathname}?${cleaned}` : location.pathname;
      navigate(nextUrl, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  if (loading) {
    return (
      <ProgressLoader
        isVisible={true}
        message="Loading settings..."
        progress={50}
      />
    );
  }

  // Redirect to general settings if no specific tab is selected
  if (location.pathname === '/settings') {
    navigate(`/settings/general${location.search || ''}`);
    return null;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="flex items-center gap-4 p-8 bg-card border-b border-foreground/10">
        <button
          className="flex items-center gap-2 bg-foreground/10 border border-foreground/20 rounded-lg px-3 py-2 text-foreground cursor-pointer transition-all duration-200 hover:bg-foreground/15 hover:border-foreground/30"
          onClick={() => navigate(-1)}
          title="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="m-0 text-3xl font-semibold">Settings - {currentTab.charAt(0).toUpperCase() + currentTab.slice(1)}</h1>
      </div>

      <div className="flex min-h-[calc(100vh-100px)] flex-col md:flex-row">
        <div className="flex-1 p-8 md:p-8 max-sm:p-4 overflow-y-auto">
          <div className="max-w-[900px]">
            <div className="bg-card rounded-xl p-8 max-sm:p-6">
              {showOneDriveConnected && (
                <div className="mb-6 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-emerald-200 text-sm flex items-center justify-between gap-4">
                  <div>
                    <div className="font-medium text-emerald-100">OneDrive connected</div>
                    <div className="text-emerald-200/80">Your OneDrive is now linked to Splitfin.</div>
                  </div>
                  <button
                    className="text-emerald-200/80 hover:text-emerald-100"
                    onClick={() => setShowOneDriveConnected(false)}
                  >
                    Dismiss
                  </button>
                </div>
              )}
              {currentTab === 'general' && <GeneralSettings userName={userName} userEmail={userEmail} userRole={userRole} />}
              {currentTab === 'profile' && (
                <ProfileSettings
                  userName={userName}
                  onProfileUpdate={(name) => setUserName(name)}
                  savingProfile={savingProfile}
                  setSavingProfile={setSavingProfile}
                />
              )}
              {currentTab === 'notifications' && <NotificationSettings />}
              {currentTab === 'database' && userRole === 'Admin' && <DatabaseSettings />}
              {currentTab === 'security' && <SecuritySettings />}
              {currentTab === 'fix-order' && userRole === 'Admin' && <FixOrder />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Admin Tools — Collapsible Section
function AdminToolsSection({ onNavigate }: { onNavigate: (path: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mb-10 last:mb-0 border border-foreground/10 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between w-full px-5 py-4 bg-foreground/[0.03] text-foreground hover:bg-foreground/5 transition-colors text-left"
      >
        <h3 className="m-0 text-base font-semibold">Admin Tools</h3>
        <span className={`text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          &#9660;
        </span>
      </button>
      {open && (
        <div className="p-4 border-t border-foreground/10">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 max-sm:grid-cols-1">
            <button
              className="flex flex-col items-center gap-2 py-6 px-4 bg-foreground/5 border border-foreground/10 rounded-lg text-foreground cursor-pointer transition-all duration-200 text-center hover:bg-foreground/[0.08] hover:border-foreground/20 hover:-translate-y-0.5"
              onClick={() => onNavigate('/settings/fix-order')}
            >
              <span className="text-xl text-primary">&#128295;</span>
              <span className="text-sm font-medium">Fix Order</span>
              <small className="text-xs text-muted-foreground">Edit and re-submit orders to Zoho</small>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// General Settings Component
function GeneralSettings({ userName, userEmail, userRole }: { userName: string; userEmail: string; userRole: string }) {
  const navigate = useNavigate();

  return (
    <div>
      <div className="mb-10 last:mb-0">
        <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">Account Information</h3>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-6 max-sm:grid-cols-1">
          <div className="flex flex-col gap-2">
            <label className="text-sm text-muted-foreground uppercase tracking-wide">Name</label>
            <p className="m-0 text-base text-foreground">{userName || 'Not set'}</p>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm text-muted-foreground uppercase tracking-wide">Email</label>
            <p className="m-0 text-base text-foreground">{userEmail}</p>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm text-muted-foreground uppercase tracking-wide">Role</label>
            <p className="m-0 inline-block px-3 py-1 bg-primary/20 text-primary rounded text-[0.9rem] capitalize">{userRole || 'User'}</p>
          </div>
        </div>
      </div>

      <div className="mb-10 last:mb-0">
        <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">Application Preferences</h3>
        <div className="flex items-center justify-between py-4 border-b border-foreground/5 max-sm:flex-col max-sm:items-start max-sm:gap-4">
          <div>
            <h4 className="m-0 mb-1 text-base text-foreground">Theme</h4>
            <p className="m-0 text-sm text-muted-foreground">Current colour theme</p>
          </div>
          <span className="px-3 py-1.5 bg-foreground/5 border border-foreground/10 rounded-md text-foreground text-sm">Dark</span>
        </div>

        <div className="flex items-center justify-between py-4 border-b border-foreground/5 max-sm:flex-col max-sm:items-start max-sm:gap-4">
          <div>
            <h4 className="m-0 mb-1 text-base text-foreground">Language</h4>
            <p className="m-0 text-sm text-muted-foreground">Display language</p>
          </div>
          <span className="px-3 py-1.5 bg-foreground/5 border border-foreground/10 rounded-md text-foreground text-sm">English</span>
        </div>

        <div className="flex items-center justify-between py-4 border-b-0 pb-0 max-sm:flex-col max-sm:items-start max-sm:gap-4">
          <div>
            <h4 className="m-0 mb-1 text-base text-foreground">Currency</h4>
            <p className="m-0 text-sm text-muted-foreground">Default currency for orders and invoices</p>
          </div>
          <span className="px-3 py-1.5 bg-foreground/5 border border-foreground/10 rounded-md text-foreground text-sm">GBP (£)</span>
        </div>
      </div>

      {/* Integrations — Coming Soon */}
      <div className="mb-10 last:mb-0">
        <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">Integrations</h3>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 max-sm:grid-cols-1">
          <div className="flex flex-col items-center gap-2 py-6 px-4 bg-foreground/[0.03] border border-dashed border-foreground/10 rounded-lg text-center opacity-60">
            <span className="text-lg">Zoho Inventory</span>
            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded text-xs font-medium">Connected</span>
          </div>
          <div className="flex flex-col items-center gap-2 py-6 px-4 bg-foreground/[0.03] border border-dashed border-foreground/10 rounded-lg text-center opacity-60">
            <span className="text-lg">Tax Settings</span>
            <span className="px-2 py-0.5 bg-muted-foreground/20 text-muted-foreground rounded text-xs font-medium">Coming soon</span>
          </div>
        </div>
      </div>

      {/* Invoice Reminders — Admin only */}
      {userRole === 'Admin' && (
        <InvoiceReminderSettings />
      )}

      {/* Admin Tools Section — Collapsible */}
      {userRole === 'Admin' && (
        <AdminToolsSection onNavigate={navigate} />
      )}
    </div>
  );
}

// Invoice Reminder Settings — master toggle + defaults
function InvoiceReminderSettings() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    invoiceService.getGlobalReminderStatus().then(setEnabled).catch(() => setEnabled(false));
  }, []);

  const handleToggle = async () => {
    if (enabled === null) return;
    const next = !enabled;
    setToggling(true);
    try {
      const result = await invoiceService.setGlobalReminderStatus(next);
      setEnabled(result);
    } catch {
      // revert
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="mb-10 last:mb-0">
      <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">Invoice Reminders</h3>

      {/* Master toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-foreground/[0.03] mb-4">
        <div>
          <h4 className="m-0 mb-1 text-[0.95rem] text-foreground">Auto-Reminders</h4>
          <p className="m-0 text-sm text-muted-foreground">
            {enabled === null ? 'Loading...' : enabled ? 'Sending daily at 8am UK time' : 'Currently disabled'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {toggling && <Loader2 size={14} className="animate-spin text-muted-foreground" />}
          <label className="relative inline-block w-12 h-6">
            <input
              type="checkbox"
              checked={enabled ?? false}
              onChange={handleToggle}
              disabled={enabled === null || toggling}
              className="opacity-0 w-0 h-0 peer"
            />
            <span className="absolute cursor-pointer inset-0 bg-foreground/10 rounded-full transition-colors duration-300 before:absolute before:content-[''] before:h-[18px] before:w-[18px] before:left-[3px] before:bottom-[3px] before:bg-muted-foreground before:rounded-full before:transition-all before:duration-300 peer-checked:bg-primary peer-checked:before:translate-x-6 peer-checked:before:bg-white peer-disabled:opacity-50" />
          </label>
        </div>
      </div>

      {/* Status banner */}
      {enabled !== null && (
        <div className={`flex items-center gap-3 mb-4 p-3 rounded-lg border ${
          enabled
            ? 'bg-emerald-500/10 border-emerald-500/20'
            : 'bg-amber-500/10 border-amber-500/20'
        }`}>
          <span className={`text-sm font-medium ${enabled ? 'text-emerald-400' : 'text-amber-400'}`}>
            {enabled ? 'Active' : 'Not active'}
          </span>
          <span className={`text-xs ${enabled ? 'text-emerald-200/70' : 'text-amber-200/70'}`}>
            {enabled
              ? 'Reminders will be sent for customers with reminders enabled.'
              : 'No reminders will be sent until you turn this on.'}
          </span>
        </div>
      )}

      {/* Default schedule info */}
      <div className="space-y-3 text-sm text-muted-foreground">
        <p className="m-0">Default schedule applied to customers without custom settings:</p>
        <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-foreground/[0.03]">
          <div>
            <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider block mb-1">Before due date</span>
            <span className="text-foreground text-[13px]">7, 3, 1 days</span>
          </div>
          <div>
            <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider block mb-1">After due date</span>
            <span className="text-foreground text-[13px]">1, 7, 14, 30 days</span>
          </div>
          <div>
            <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider block mb-1">Max per invoice</span>
            <span className="text-foreground text-[13px]">5 reminders</span>
          </div>
          <div>
            <span className="text-[11px] text-muted-foreground/60 uppercase tracking-wider block mb-1">CC agent</span>
            <span className="text-foreground text-[13px]">Yes</span>
          </div>
        </div>
        <p className="m-0 text-xs text-muted-foreground/70">Override per customer from their detail page.</p>
      </div>
    </div>
  );
}

// Profile Settings Component
function ProfileSettings({
  userName,
  onProfileUpdate,
  savingProfile,
  setSavingProfile
}: {
  userName: string;
  onProfileUpdate: (name: string) => void;
  savingProfile: boolean;
  setSavingProfile: (saving: boolean) => void;
}) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    department: ''
  });

  useEffect(() => {
    // Initialize form with current user name
    const names = userName.split(' ');
    setFormData({
      firstName: names[0] || '',
      lastName: names.slice(1).join(' ') || '',
      phone: '',
      department: ''
    });
  }, [userName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);

    try {
      // Profile editing not yet supported via API
      onProfileUpdate(`${formData.firstName} ${formData.lastName}`.trim());
      alert('Profile update saved locally. Backend support coming soon.');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div>
      <div className="mb-10 last:mb-0">
        <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">Profile Information</h3>
        <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-2">
            <label htmlFor="firstName" className="text-[0.9rem] text-foreground font-medium">First Name</label>
            <input
              type="text"
              id="firstName"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              placeholder="Enter your first name"
              className="px-4 py-3 bg-foreground/5 border border-foreground/10 rounded-md text-foreground text-[0.95rem] transition-all duration-200 focus:outline-none focus:border-primary focus:bg-foreground/[0.08]"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="lastName" className="text-[0.9rem] text-foreground font-medium">Last Name</label>
            <input
              type="text"
              id="lastName"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              placeholder="Enter your last name"
              className="px-4 py-3 bg-foreground/5 border border-foreground/10 rounded-md text-foreground text-[0.95rem] transition-all duration-200 focus:outline-none focus:border-primary focus:bg-foreground/[0.08]"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="phone" className="text-[0.9rem] text-foreground font-medium">Phone Number</label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="Enter your phone number"
              className="px-4 py-3 bg-foreground/5 border border-foreground/10 rounded-md text-foreground text-[0.95rem] transition-all duration-200 focus:outline-none focus:border-primary focus:bg-foreground/[0.08]"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="department" className="text-[0.9rem] text-foreground font-medium">Department</label>
            <input
              type="text"
              id="department"
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              placeholder="Enter your department"
              className="px-4 py-3 bg-foreground/5 border border-foreground/10 rounded-md text-foreground text-[0.95rem] transition-all duration-200 focus:outline-none focus:border-primary focus:bg-foreground/[0.08]"
            />
          </div>

          <button
            type="submit"
            className="self-start px-8 py-3 bg-primary text-white border-none rounded-lg text-base font-medium cursor-pointer transition-all duration-200 hover:brightness-90 hover:-translate-y-px hover:shadow-[0_4px_12px_var(--primary)/30] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={savingProfile}
          >
            {savingProfile ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Notification Settings Component
function NotificationSettings() {
  const [notifications, setNotifications] = useState({
    orderUpdates: true,
    newCustomers: true,
    lowInventory: false,
    dailyReports: true,
    weeklyReports: false
  });

  const handleToggle = (key: keyof typeof notifications) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const ToggleSwitch = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <label className="relative inline-block w-12 h-6">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="opacity-0 w-0 h-0 peer"
      />
      <span className="absolute cursor-pointer inset-0 bg-foreground/10 rounded-full transition-colors duration-300 before:absolute before:content-[''] before:h-[18px] before:w-[18px] before:left-[3px] before:bottom-[3px] before:bg-muted-foreground before:rounded-full before:transition-all before:duration-300 peer-checked:bg-primary peer-checked:before:translate-x-6 peer-checked:before:bg-white" />
    </label>
  );

  return (
    <div>
      <div className="mb-10 last:mb-0">
        <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">Email Notifications</h3>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between p-4 bg-foreground/[0.03] rounded-lg transition-all duration-200 hover:bg-foreground/5">
            <div>
              <h4 className="m-0 mb-1 text-[0.95rem] text-foreground">Order Updates</h4>
              <p className="m-0 text-sm text-muted-foreground">Receive notifications when order status changes</p>
            </div>
            <ToggleSwitch checked={notifications.orderUpdates} onChange={() => handleToggle('orderUpdates')} />
          </div>

          <div className="flex items-center justify-between p-4 bg-foreground/[0.03] rounded-lg transition-all duration-200 hover:bg-foreground/5">
            <div>
              <h4 className="m-0 mb-1 text-[0.95rem] text-foreground">New Customers</h4>
              <p className="m-0 text-sm text-muted-foreground">Get notified when new customers are added</p>
            </div>
            <ToggleSwitch checked={notifications.newCustomers} onChange={() => handleToggle('newCustomers')} />
          </div>

          <div className="flex items-center justify-between p-4 bg-foreground/[0.03] rounded-lg transition-all duration-200 hover:bg-foreground/5">
            <div>
              <h4 className="m-0 mb-1 text-[0.95rem] text-foreground">Low Inventory Alerts</h4>
              <p className="m-0 text-sm text-muted-foreground">Alerts when inventory falls below threshold</p>
            </div>
            <ToggleSwitch checked={notifications.lowInventory} onChange={() => handleToggle('lowInventory')} />
          </div>
        </div>
      </div>

      <div className="mb-10 last:mb-0">
        <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">Report Emails</h3>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between p-4 bg-foreground/[0.03] rounded-lg transition-all duration-200 hover:bg-foreground/5">
            <div>
              <h4 className="m-0 mb-1 text-[0.95rem] text-foreground">Daily Reports</h4>
              <p className="m-0 text-sm text-muted-foreground">Receive daily sales and activity reports</p>
            </div>
            <ToggleSwitch checked={notifications.dailyReports} onChange={() => handleToggle('dailyReports')} />
          </div>

          <div className="flex items-center justify-between p-4 bg-foreground/[0.03] rounded-lg transition-all duration-200 hover:bg-foreground/5">
            <div>
              <h4 className="m-0 mb-1 text-[0.95rem] text-foreground">Weekly Summary</h4>
              <p className="m-0 text-sm text-muted-foreground">Get weekly performance summaries</p>
            </div>
            <ToggleSwitch checked={notifications.weeklyReports} onChange={() => handleToggle('weeklyReports')} />
          </div>
        </div>
      </div>
    </div>
  );
}

// Database Settings Component - Admin only
// Thumbnail generator — admin batch tool. Walks the R2 bucket and writes a
// small `_thumb.webp` next to each product image so the trade portal can load
// card images at a fraction of the memory.
function ThumbnailGenerator() {
  const [prefix, setPrefix] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [overwrite, setOverwrite] = useState(false);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totals, setTotals] = useState({ processed: 0, generated: 0, skipped: 0, failed: 0, batches: 0 });
  const stopRef = useRef(false);

  const run = async () => {
    setRunning(true);
    setDone(false);
    setError(null);
    setTotals({ processed: 0, generated: 0, skipped: 0, failed: 0, batches: 0 });
    stopRef.current = false;
    let token: string | null | undefined;
    try {
      do {
        const r = await api.post<{
          processed: number; generated: number; skipped: number; failed: number; nextToken: string | null;
        }>('/api/v1/images/generate-thumbnails', {
          prefix,
          continuationToken: token,
          maxKeys: 30,
          dryRun,
          overwrite,
        });
        setTotals((t) => ({
          processed: t.processed + r.processed,
          generated: t.generated + r.generated,
          skipped: t.skipped + r.skipped,
          failed: t.failed + r.failed,
          batches: t.batches + 1,
        }));
        token = r.nextToken;
      } while (token && !stopRef.current);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mb-10 last:mb-0">
      <h3 className="m-0 mb-2 text-lg font-semibold text-foreground">Product Image Thumbnails</h3>
      <p className="text-muted-foreground m-0 mb-4 leading-relaxed text-sm">
        Generate small <code>_thumb.webp</code> variants in R2 so the trade portal loads card images at a fraction of
        the memory. Safe to re-run — existing thumbnails are skipped. Leave the prefix empty for the whole bucket, or
        scope it (e.g. <code>images/ppd-paperproducts-design-gmbh/</code>).
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="flex flex-col gap-1 text-sm text-muted-foreground">
          R2 key prefix (optional)
          <input
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            placeholder="images/"
            disabled={running}
            className="px-3 py-2 bg-foreground/5 border border-foreground/10 rounded-md text-foreground text-sm min-w-[280px] disabled:opacity-50"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} disabled={running} />
          Dry run
        </label>
        <label className="flex items-center gap-2 text-sm text-foreground">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} disabled={running} />
          Overwrite existing
        </label>
        {!running ? (
          <button
            onClick={run}
            className="py-2 px-4 bg-primary text-white rounded-md text-sm font-medium cursor-pointer hover:brightness-90"
          >
            {dryRun ? 'Preview' : 'Generate thumbnails'}
          </button>
        ) : (
          <button
            onClick={() => { stopRef.current = true; }}
            className="py-2 px-4 bg-foreground/10 text-foreground rounded-md text-sm font-medium cursor-pointer hover:bg-foreground/20 inline-flex items-center gap-2"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Stop after batch
          </button>
        )}
      </div>

      {(running || done || totals.batches > 0) && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-3 mb-3">
          {([
            ['Batches', totals.batches],
            ['Scanned', totals.processed],
            [dryRun ? 'Would make' : 'Generated', totals.generated],
            ['Skipped', totals.skipped],
            ['Failed', totals.failed],
          ] as const).map(([label, value]) => (
            <div key={label} className="bg-foreground/5 border border-foreground/10 rounded-lg px-3 py-3 text-center">
              <div className="text-lg font-semibold text-foreground tabular-nums">{value}</div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
      )}

      {running && <p className="text-sm text-muted-foreground m-0">Working… leave this tab open until it finishes.</p>}
      {done && !running && (
        <p className="text-sm text-emerald-400 m-0">
          Done. {totals.generated} thumbnails {dryRun ? 'would be created' : 'created'}, {totals.skipped} already existed.
        </p>
      )}
      {error && <p className="text-sm text-red-400 m-0">Error: {error}</p>}
    </div>
  );
}

// Orphan image reconciler — removes product_images rows whose R2 object no longer
// exists (e.g. deleted directly in Cloudflare). Runs automatically daily; this is
// the on-demand trigger.
function ImageReconciler() {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ r2Objects: number; dbRows: number; orphansDeleted: number } | null>(null);

  const run = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      setResult(await imageService.reconcile());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mb-10 last:mb-0">
      <h3 className="m-0 mb-2 text-lg font-semibold text-foreground">Reconcile Image Records</h3>
      <p className="text-muted-foreground m-0 mb-4 leading-relaxed text-sm">
        Remove database image records whose file no longer exists in Cloudflare R2 (e.g. deleted directly in the
        Cloudflare dashboard). This also clears the product&apos;s image so it shows as missing again. Runs automatically
        every night at 3am — use this to run it now.
      </p>

      <button
        onClick={run}
        disabled={running}
        className="py-2 px-4 bg-primary text-white rounded-md text-sm font-medium cursor-pointer hover:brightness-90 disabled:opacity-50 inline-flex items-center gap-2"
      >
        {running && <Loader2 className="w-4 h-4 animate-spin" />}
        {running ? 'Reconciling…' : 'Reconcile now'}
      </button>

      {result && (
        <p className="text-sm text-emerald-400 m-0 mt-3">
          Done. Checked {result.r2Objects} R2 files against {result.dbRows} records — removed {result.orphansDeleted} orphaned record(s).
        </p>
      )}
      {error && <p className="text-sm text-red-400 m-0 mt-3">Error: {error}</p>}
    </div>
  );
}

function DatabaseSettings() {
  const [activeTab, setActiveTab] = useState<'maintenance' | 'export'>('maintenance');

  return (
    <div>
      <div className="mb-10 last:mb-0">
        <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">Database Management</h3>
        <p className="text-muted-foreground m-0 mb-6 leading-relaxed">
          Manage database operations and maintenance tasks. These operations should be performed during off-peak hours.
          <br /><small className="text-muted-foreground mt-2 block">Access restricted to Admins</small>
        </p>
      </div>

      {/* Database Tools Tabs */}
      <div className="mb-10 last:mb-0">
        <div className="flex gap-2 mb-6 bg-foreground/[0.03] p-1 rounded-lg">
          <button
            className={`flex-1 py-3 px-4 border-none rounded-md text-[0.9rem] font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
              activeTab === 'maintenance'
                ? 'bg-primary text-white hover:brightness-90'
                : 'bg-transparent text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
            }`}
            onClick={() => setActiveTab('maintenance')}
          >
            Database Maintenance
          </button>
          <button
            className={`flex-1 py-3 px-4 border-none rounded-md text-[0.9rem] font-medium cursor-pointer transition-all duration-200 whitespace-nowrap ${
              activeTab === 'export'
                ? 'bg-primary text-white hover:brightness-90'
                : 'bg-transparent text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
            }`}
            onClick={() => setActiveTab('export')}
          >
            Data Export
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'maintenance' && (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 max-sm:grid-cols-1">
            <button className="flex flex-col items-center gap-2 py-8 px-4 bg-foreground/5 border border-foreground/10 rounded-lg text-foreground cursor-pointer transition-all duration-200 text-center hover:enabled:bg-foreground/[0.08] hover:enabled:border-foreground/20 hover:enabled:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:text-primary" onClick={() => alert('Feature coming soon')}>
              <span className="text-2xl text-primary">&#128295;</span>
              <span className="text-base font-medium">Optimize Tables</span>
              <small className="text-xs text-muted-foreground">Improve database performance</small>
            </button>

            <button className="flex flex-col items-center gap-2 py-8 px-4 bg-foreground/5 border border-foreground/10 rounded-lg text-foreground cursor-pointer transition-all duration-200 text-center hover:enabled:bg-foreground/[0.08] hover:enabled:border-foreground/20 hover:enabled:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:text-primary" onClick={() => alert('Feature coming soon')}>
              <span className="text-2xl text-primary">&#128465;&#65039;</span>
              <span className="text-base font-medium">Clean Up Data</span>
              <small className="text-xs text-muted-foreground">Remove old records</small>
            </button>

            <button className="flex flex-col items-center gap-2 py-8 px-4 bg-foreground/5 border border-foreground/10 rounded-lg text-foreground cursor-pointer transition-all duration-200 text-center hover:enabled:bg-foreground/[0.08] hover:enabled:border-foreground/20 hover:enabled:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:text-primary" onClick={() => alert('Feature coming soon')}>
              <span className="text-2xl text-primary">&#9851;&#65039;</span>
              <span className="text-base font-medium">Rebuild Indexes</span>
              <small className="text-xs text-muted-foreground">Optimize query performance</small>
            </button>
          </div>
        )}

        {activeTab === 'export' && (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4 max-sm:grid-cols-1">
            <button className="flex flex-col items-center gap-2 py-8 px-4 bg-foreground/5 border border-foreground/10 rounded-lg text-foreground cursor-pointer transition-all duration-200 text-center hover:enabled:bg-foreground/[0.08] hover:enabled:border-foreground/20 hover:enabled:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:text-primary" onClick={() => alert('Feature coming soon')}>
              <span className="text-2xl text-primary">&#128202;</span>
              <span className="text-base font-medium">Export Orders</span>
              <small className="text-xs text-muted-foreground">Download order data</small>
            </button>

            <button className="flex flex-col items-center gap-2 py-8 px-4 bg-foreground/5 border border-foreground/10 rounded-lg text-foreground cursor-pointer transition-all duration-200 text-center hover:enabled:bg-foreground/[0.08] hover:enabled:border-foreground/20 hover:enabled:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:text-primary" onClick={() => alert('Feature coming soon')}>
              <span className="text-2xl text-primary">&#128101;</span>
              <span className="text-base font-medium">Export Customers</span>
              <small className="text-xs text-muted-foreground">Download customer data</small>
            </button>

            <button className="flex flex-col items-center gap-2 py-8 px-4 bg-foreground/5 border border-foreground/10 rounded-lg text-foreground cursor-pointer transition-all duration-200 text-center hover:enabled:bg-foreground/[0.08] hover:enabled:border-foreground/20 hover:enabled:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:text-primary" onClick={() => alert('Feature coming soon')}>
              <span className="text-2xl text-primary">&#128230;</span>
              <span className="text-base font-medium">Export Inventory</span>
              <small className="text-xs text-muted-foreground">Download inventory data</small>
            </button>
          </div>
        )}
      </div>

      <ThumbnailGenerator />
      <ImageReconciler />
    </div>
  );
}

// Security Settings Component
function SecuritySettings() {
  const handlePinChange = () => {
    alert('PIN change functionality coming soon. Contact an admin to reset your PIN.');
  };

  return (
    <div>
      <div className="mb-10 last:mb-0">
        <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">PIN Authentication</h3>
        <p className="text-muted-foreground m-0 mb-6 leading-relaxed">
          Your account uses a PIN for authentication.
        </p>
        <button
          className="px-6 py-3 bg-primary text-white border-none rounded-lg text-[0.95rem] font-medium cursor-pointer transition-all duration-200 hover:enabled:brightness-90 hover:enabled:-translate-y-px hover:enabled:shadow-[0_4px_12px_var(--primary)/30] disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handlePinChange}
        >
          Change PIN
        </button>
      </div>

      <div className="mb-10 last:mb-0">
        <h3 className="m-0 mb-4 text-lg font-semibold text-foreground">Active Sessions</h3>
        <p className="text-muted-foreground m-0 mb-6 leading-relaxed">
          View and manage your active sessions across different devices.
        </p>
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between p-4 bg-foreground/[0.03] rounded-lg">
            <div>
              <h4 className="m-0 mb-1 text-[0.95rem] text-foreground">Current Session</h4>
              <p className="m-0 text-sm text-muted-foreground">Active now</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
